/**
 * Feed de actividad reciente del dashboard de Tesorería.
 *
 * Consume EventoActividad del backend. Renderiza una lista compacta
 * con icono de tipo + descripcion + monto opcional + timestamp
 * relativo. Cada item es clickable si tiene `enlace`.
 */
import { Link } from 'react-router-dom';
import type { EventoActividad } from '../../lib/api/dashboard-tesoreria';
import { Monto } from './Monto';

const ICONO_POR_TIPO: Record<EventoActividad['tipo'], string> = {
  pago_aprobado: '✓',
  gasto_registrado: '−',
  compensacion_aplicada: '⇄',
  suspension: '!',
  cierre_firmado: '◯',
};

const COLOR_POR_TIPO: Record<EventoActividad['tipo'], string> = {
  pago_aprobado: 'text-emerald-400 bg-emerald-400/10',
  gasto_registrado: 'text-red-400 bg-red-400/10',
  compensacion_aplicada: 'text-purple-300 bg-purple-300/10',
  suspension: 'text-amber-400 bg-amber-400/10',
  cierre_firmado: 'text-blue-400 bg-blue-400/10',
};

function fechaRelativa(iso: string): string {
  const ahora = Date.now();
  const ts = new Date(iso).getTime();
  const diff = ahora - ts;
  const min = Math.floor(diff / 60_000);
  if (min < 1) return 'ahora';
  if (min < 60) return `hace ${min} min`;
  const horas = Math.floor(min / 60);
  if (horas < 24) return `hace ${horas} h`;
  const dias = Math.floor(horas / 24);
  if (dias < 30) return `hace ${dias} d`;
  return new Date(iso).toLocaleDateString('es-CL', {
    day: '2-digit',
    month: 'short',
  });
}

export interface FeedActividadProps {
  eventos: EventoActividad[];
  loading?: boolean;
}

export function FeedActividad({ eventos, loading }: FeedActividadProps) {
  if (loading) {
    return (
      <div className="text-sm text-text-muted">Cargando actividad…</div>
    );
  }
  if (eventos.length === 0) {
    return (
      <div className="text-sm text-text-muted">
        Sin actividad registrada todavía.
      </div>
    );
  }
  return (
    <ul className="divide-y divide-white/[0.06]">
      {eventos.map((e) => {
        const content = (
          <div className="py-2 flex items-center gap-3">
            <span
              className={`w-7 h-7 rounded-full flex items-center justify-center text-sm flex-shrink-0 ${COLOR_POR_TIPO[e.tipo]}`}
              aria-hidden="true"
            >
              {ICONO_POR_TIPO[e.tipo]}
            </span>
            <div className="flex-1 min-w-0">
              <div className="text-sm text-text-primary truncate">
                {e.descripcion}
              </div>
              <div className="text-[11px] text-text-muted">
                {fechaRelativa(e.timestamp)}
                {e.lote_identificador
                  ? ` · Casa ${e.lote_identificador}`
                  : ''}
              </div>
            </div>
            {e.monto !== null && (
              <div className="text-sm text-text-primary text-right whitespace-nowrap">
                <Monto value={e.monto} />
              </div>
            )}
          </div>
        );

        return (
          <li key={e.entidad_id + e.tipo}>
            {e.enlace ? (
              <Link
                to={e.enlace}
                className="block hover:bg-white/[0.03] -mx-2 px-2 rounded transition-colors"
              >
                {content}
              </Link>
            ) : (
              content
            )}
          </li>
        );
      })}
    </ul>
  );
}
